import { IHttpService, IPromise } from "angular";
import { IcdOfficeService } from "./icd-office.service";
import {
    ICDCorporationListRM, IcdCorporationResponse,
    IcdCorporationManager, OrganizationModuleModel,
    IcdCorporationUpdateRequest, IcdCorporationStatusRM, PagedEntityList
} from "@herbjoy/apex-api-models";

export enum IcdCorporationStatus {
    Active = 1,
    Inactive = 2
}

export class IcdCorporationService {
    constructor(private $http: IHttpService, private apexApiUrl: string, private icdOfficeService: IcdOfficeService) {
        'ngInject'
    }

    retrieveIcdCorporations(query) {
        return this.$http<ICDCorporationListRM[]>({
            method: "GET",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            url: this.apexApiUrl + "corporations",
            params: query || {}
        }).then(response => {
            return response.data;
        });
    }

    retrieveIcdCorporationsPage(pageIndex: number, query) {
        return this.$http<PagedEntityList<ICDCorporationListRM[]>>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "corporations" + "/page/" + pageIndex || "0",
            params: query,
        })
            .then(response => {
                return response.data;
            });
    }

    retrieveIcdCorporationsByModuleAdmin(moduleName: string, query?) {
        return this.$http<ICDCorporationListRM[]>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "corporations/module/" + moduleName,
            params: query || {}
        }).then(response => {
            return response.data;
        })
    }

    /**
     * Retrieve icd corporations that are managed by the current user.
     * @returns {any} The list of corporations
     */
    retrieveManagedIcdCorporations(query) {
        return this.$http<ICDCorporationListRM[]>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "corporations/managed",
            params: query || {}
        }).then(response => {
            return response.data;
        })
    }

    /**
     * Retrieve the icd corporation with the given ID.
     * @param {number} icdCorporationID The corporationID
     * @returns {any} The specified corporation.
     */
    retrieveIcdCorporation(icdCorporationID: number): IPromise<IcdCorporationResponse> {
        return this.$http<IcdCorporationResponse>({
            method: "GET",
            url: this.apexApiUrl + "corporations/" + icdCorporationID,
            headers: { Accept: "application/json" }
        })
            .then(response => {
                return response.data;
            });
    }

    retrieveModuleAdminIcdOffices(employeeID, moduleID) {
        const icdOffices: any = {};
        icdOffices.moduleAdminID = employeeID;
        icdOffices.moduleID = moduleID;
        return this.icdOfficeService.retrieveIcdOffices(icdOffices);
    }

    retrieveIcdOffices(icdCorporationID, moduleAdminID, moduleID) {
        // TODO: Replace all references to this service method with the icd office service.
        const icdOffices: any = {};
        icdOffices.icdCorporationID = icdCorporationID;
        icdOffices.moduleAdminID = moduleAdminID;
        icdOffices.moduleID = moduleID;
        return this.icdOfficeService.retrieveIcdOffices(icdOffices);
    }

    /**
     * Retrieve a list of all auditors for the corporation.
     * @param {number} icdCorporationID The corporation whose data will be loaded
     * @returns {any} The list of auditors for the corporation.
     */
    retrieveIcdCorporationAuditors(icdCorporationID: number): IPromise<IcdCorporationManager> {
        return this.$http.get(this.apexApiUrl + "corporations/" + icdCorporationID + "/auditors", { headers: { Accept: "applicationCache/json" } })
            .then(response => {
                return response.data;
            });
    }

    retrieveIcdCompanyLogo(icdCorporationID) {
        return this.$http.get(this.apexApiUrl + "corporations/" + icdCorporationID + "/logo", { responseType: 'blob' }
        ).then(results => {
            return results;
        }, results => {
            console.log(results);
        });
    }

    retrieveIcdModules(): IPromise<OrganizationModuleModel> {
        return this.$http<OrganizationModuleModel>({
            method: "GET",
            url: this.apexApiUrl + "icd-Modules",
            headers: { Accept: "application/json" }
        })
            .then(response => {
                return response.data;
            });
    }

    updateIcdCorporationService(icdCorporationID: number, icdCorporation: IcdCorporationUpdateRequest) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "corporations/" + icdCorporationID,
            data: icdCorporation,
            headers: { "Content-Type": "application/json" }
        });
    }

    createIcdCorporationService(icdCorporation: IcdCorporationUpdateRequest) {
        return this.$http({
            method: "POST",
            url: this.apexApiUrl + "corporations",
            data: icdCorporation,
            headers: { "Content-Type": "application/json" }
        }).then(response => {
            return response.data;
        });
    }

    retrieveIcdCorporationStatus(): IPromise<IcdCorporationStatusRM[]> {
        return this.$http<IcdCorporationStatusRM[]>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "corporation-statuses"
        })
            .then(response => {
                return response.data;
            });
    }

    deleteIcdCorporation(icdCorporationID: number) {
        return this.$http({
            method: "DELETE",
            url: this.apexApiUrl + "corporations/" + icdCorporationID
        });
    }
}

export function sortIcdCorporationByOwner(a, b) {
    if (a.Owner < b.Owner) {
        return -1;
    } else if (a.Owner > b.Owner) {
        return 1;
    }
    if (a.ClientName < b.ClientName) {
        return -1;
    } else if (a.ClientName < b.ClientName) {
        return 1;
    }
    return 0;
}


export function sortIcdCorporationByAccess(p, q) {
    // Show updateable companies before read-only companies.
    if (p.Access.CanUpdate !== q.Access.CanUpdate) {
        return q.Access.CanUpdate - p.Access.CanUpdate;
    }
    return sortIcdCorporationByOwner(p, q);
}
